import { PairingError, type Pair } from './types.js';

export interface RoundRobinRound {
  readonly round: number;
  /** Pairs by pairing number, in board order. */
  readonly pairs: readonly Pair[];
  /** The player with the bye this round (odd player count), or null. */
  readonly bye: number | null;
}

/**
 * FIDE Berger tables (Handbook C.05, Annex 1) for n players.
 *
 * With an odd number of players a dummy player n+1 is added; whoever meets
 * the dummy has the bye. Board 1 always holds the game of the highest number.
 */
export function bergerSchedule(n: number): RoundRobinRound[] {
  if (!Number.isInteger(n) || n < 2) throw new PairingError('round robin needs at least 2 players');
  const N = n % 2 === 0 ? n : n + 1;
  const m = N - 1;
  const wrap = (x: number): number => ((((x - 1) % m) + m) % m) + 1;
  const rounds: RoundRobinRound[] = [];
  for (let r = 1; r <= m; r++) {
    const pivot = (((r - 1) * (N / 2)) % m) + 1;
    const pairs: Pair[] = [];
    let bye: number | null = null;
    const first: Pair = r % 2 === 1 ? { white: pivot, black: N } : { white: N, black: pivot };
    if (N > n) bye = pivot;
    else pairs.push(first);
    for (let k = 1; k < N / 2; k++) {
      pairs.push({ white: wrap(pivot + k), black: wrap(pivot - k) });
    }
    rounds.push({ round: r, pairs, bye });
  }
  return rounds;
}

/**
 * Double round robin: the first cycle, then the same rounds with colours
 * reversed. When `swapLastTwo` is set (FIDE's recommendation), the last two
 * rounds of the first cycle are swapped so nobody has the same colour three
 * times in a row across the cycle boundary.
 */
export function doubleBergerSchedule(n: number, swapLastTwo = true): RoundRobinRound[] {
  const first = bergerSchedule(n);
  if (swapLastTwo && first.length >= 2) {
    const a = first[first.length - 2] as RoundRobinRound;
    const b = first[first.length - 1] as RoundRobinRound;
    first[first.length - 2] = b;
    first[first.length - 1] = a;
  }
  const cycle1 = first.map((r, i) => ({ ...r, round: i + 1 }));
  const cycle2 = cycle1.map((r, i) => ({
    round: cycle1.length + i + 1,
    pairs: r.pairs.map((p) => ({ white: p.black, black: p.white })),
    bye: r.bye,
  }));
  return [...cycle1, ...cycle2];
}

/**
 * Scheveningen system: every member of team A plays every member of team B.
 * Both teams have k players; returns k rounds. Team A's players are 1..k and
 * team B's are k+1..2k. Colours alternate by board and round.
 */
export function scheveningenSchedule(k: number): RoundRobinRound[] {
  if (!Number.isInteger(k) || k < 1) throw new PairingError('Scheveningen needs team size >= 1');
  const rounds: RoundRobinRound[] = [];
  for (let r = 0; r < k; r++) {
    const pairs: Pair[] = [];
    for (let board = 0; board < k; board++) {
      const a = board + 1;
      const b = k + ((board + r) % k) + 1;
      const aWhite = (board + r) % 2 === 0;
      pairs.push(aWhite ? { white: a, black: b } : { white: b, black: a });
    }
    rounds.push({ round: r + 1, pairs, bye: null });
  }
  return rounds;
}
